/**
 * TaskProgress - Progress indicator for task completion.
 *
 * Shows progress bar and field status chips.
 */

import React from 'react';
import { Box, Typography, LinearProgress, Chip } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { colors } from '../../theme/colors';

export interface FieldStatus {
  key: string;
  label: string;
  required: boolean;
  completed: boolean;
}

export interface TaskProgressProps {
  progressPercent: number;
  progressSummary?: string;
  fields?: FieldStatus[];
  showFieldChips?: boolean;
}

export const TaskProgress: React.FC<TaskProgressProps> = ({
  progressPercent,
  progressSummary,
  fields = [],
  showFieldChips = true,
}) => {
  return (
    <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
      {/* Progress bar */}
      <Box sx={{ mb: showFieldChips && fields.length > 0 ? 1.5 : 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {progressSummary || 'Getting started...'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {progressPercent}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: colors.gray[200],
            '& .MuiLinearProgress-bar': {
              bgcolor: progressPercent === 100 ? colors.green[600] : colors.blue[500],
              borderRadius: 3,
            },
          }}
        />
      </Box>

      {/* Field chips */}
      {showFieldChips && fields.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {fields.map((field) => (
            <Chip
              key={field.key}
              label={field.label}
              size="small"
              variant={field.completed ? 'filled' : 'outlined'}
              color={field.completed ? 'success' : 'default'}
              icon={field.completed ? <CheckIcon sx={{ fontSize: 14 }} /> : undefined}
              sx={{
                fontSize: '0.75rem',
                height: 22,
                '& .MuiChip-icon': { fontSize: 14 },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TaskProgress;
